import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../invoiceForm';
import { InvoiceDaySummary, InvoicePaymentTotals } from '../types/Invoice';
import styles from '../Invoice.module.css';

type InvoiceDaySummaryPanelProps = {
  summary: InvoiceDaySummary;
  currency?: string;
};

const MIX_KEYS = ['cash', 'card', 'bank', 'other'] as const;

const MIX_LABEL: Record<(typeof MIX_KEYS)[number], string> = {
  cash: 'methodCash',
  card: 'methodCard',
  bank: 'methodBank',
  other: 'methodOther',
};

const MIX_SEG: Record<(typeof MIX_KEYS)[number], string> = {
  cash: styles.mixSegCash,
  card: styles.mixSegCard,
  bank: styles.mixSegBank,
  other: styles.mixSegOther,
};

const PaymentMix: React.FC<{ totals: InvoicePaymentTotals; currency?: string }> = ({ totals, currency }) => {
  const { t } = useTranslation('invoices');
  const segments = MIX_KEYS
    .map(key => ({ key, amount: totals[key] }))
    .filter(segment => segment.amount > 0);

  return (
    <div className={styles.paymentMix}>
      <div className={styles.mixTrack}>
        {segments.length === 0 ? (
          <span className={styles.mixEmpty} />
        ) : (
          segments.map(segment => (
            <span
              key={segment.key}
              className={`${styles.mixSeg} ${MIX_SEG[segment.key]}`}
              style={{ flexGrow: Math.max(segment.amount, 0.01) }}
            />
          ))
        )}
      </div>
      <div className={styles.mixLegend}>
        {segments.length === 0 ? (
          <span className={styles.mixLegendItem}>
            {t('moneyInTotal')}
            <strong>{formatMoney(0, currency)}</strong>
          </span>
        ) : (
          segments.map(segment => (
            <span key={segment.key} className={styles.mixLegendItem}>
              <i className={`${styles.mixDot} ${MIX_SEG[segment.key]}`} />
              {t(MIX_LABEL[segment.key])}
              <strong>{formatMoney(segment.amount, currency)}</strong>
            </span>
          ))
        )}
      </div>
    </div>
  );
};

export const InvoiceDaySummaryPanel: React.FC<InvoiceDaySummaryPanelProps> = ({ summary, currency: fallbackCurrency }) => {
  const { t } = useTranslation('invoices');
  const currency = summary.currency || fallbackCurrency;

  return (
    <div className={styles.dayBoard}>
      {summary.mixedCurrencies ? (
        <p className={styles.dayCurrencyWarning} role="status">
          {t('mixedCurrencyWarning', { currencies: summary.currencies.join(', ') })}
        </p>
      ) : null}
      <article className={styles.dayHero}>
        <p className={styles.dayKpiLabel}>{t('moneyIn')}</p>
        <p className={styles.dayHeroValue}>{formatMoney(summary.moneyIn.total, currency)}</p>
        <PaymentMix totals={summary.moneyIn} currency={currency} />
      </article>
      <div className={styles.dayKpis}>
        <article className={styles.dayKpi}>
          <p className={styles.dayKpiLabel}>{t('issuedThisDay')}</p>
          <p className={styles.dayKpiValue}>{formatMoney(summary.issued.billedTotal, currency)}</p>
          <p className={styles.dayKpiMeta}>{t('issuedCount', { count: summary.issued.count })}</p>
        </article>
        <article className={styles.dayKpi}>
          <p className={styles.dayKpiLabel}>{t('remaining')}</p>
          <p className={styles.dayKpiValue}>{formatMoney(summary.issued.remaining, currency)}</p>
          <p className={styles.dayKpiMeta}>{t('stillOpen')}</p>
        </article>
        <article className={styles.dayKpi}>
          <p className={styles.dayKpiLabel}>{t('collectedOnEarlier')}</p>
          <p className={styles.dayKpiValue}>{formatMoney(summary.collectedOnEarlier.total, currency)}</p>
          <p className={styles.dayKpiMeta}>{t('fromEarlier')}</p>
        </article>
        <article className={styles.dayKpi}>
          <p className={styles.dayKpiLabel}>{t('draftsThisDay')}</p>
          <p className={styles.dayKpiValue}>{summary.draftCount}</p>
          <p className={styles.dayKpiMeta}>{t('draftCountLabel', { count: summary.draftCount })}</p>
        </article>
      </div>
    </div>
  );
};
